import { ChevronDown } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { SectionCard } from '@/components/SectionCard';
import { Text } from '@/components/Text';
import { colors, spacing } from '@/constants/theme';

export interface FaqCardItem {
  q: string;
  a: string;
}

export interface FaqCardProps {
  /** Card label, e.g. "FAQ" or "Stuff your friends will ask". */
  title: string;
  /** One-line context under the title. */
  subtitle?: string;
  /** Server-authored `marketing_copy.faq`. Renders nothing when empty. */
  items: readonly FaqCardItem[] | null | undefined;
  testID?: string;
}

/**
 * The one FAQ treatment: a SectionCard, then hairline-divided
 * question rows that expand in place. Used on the deal page ("FAQ") and on
 * the deal referral screen ("Stuff your friends will ask"), so a deal's
 * questions look the same wherever they appear.
 *
 * First item open by default. Toggling is independent per item — a reader
 * may want to compare a few answers side-by-side without collapsing the one
 * above.
 */
export function FaqCard({ title, subtitle, items, testID }: FaqCardProps) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set([0]));

  const toggle = useCallback((index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <SectionCard title={title} subtitle={subtitle} testID={testID}>
      <View>
        {items.map((item, index) => (
          <FaqItem
            key={item.q}
            question={item.q}
            answer={item.a}
            expanded={expanded.has(index)}
            onToggle={() => toggle(index)}
            isLast={index === items.length - 1}
          />
        ))}
      </View>
    </SectionCard>
  );
}

interface FaqItemProps {
  question: string;
  answer: string;
  expanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}

function FaqItem({ question, answer, expanded, onToggle, isLast }: FaqItemProps) {
  return (
    <View style={[styles.item, !isLast && styles.itemDivider]}>
      <Pressable
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityLabel={question}
        accessibilityState={{ expanded }}
        style={({ pressed }) => [styles.questionRow, pressed && styles.questionPressed]}
      >
        <Text variant="bodyBold" color="deepGreen" style={styles.question}>
          {question}
        </Text>
        <ChevronDown
          size={18}
          color={colors.textMuted}
          strokeWidth={2}
          style={expanded ? styles.chevronOpen : undefined}
        />
      </Pressable>
      {expanded ? (
        <Text variant="body" color="text" style={styles.answer}>
          {answer}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  item: {
    paddingVertical: spacing.md,
  },
  itemDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  questionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  questionPressed: {
    opacity: 0.7,
  },
  question: {
    flex: 1,
    lineHeight: 22,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  answer: {
    marginTop: spacing.sm,
    lineHeight: 22,
  },
});
